import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { formatMoney } from '@johnsaks/core'
import { useEffect, useState } from 'react'
import { BtnGhost, BtnPrimary, Field, Select, TextArea, TextInput } from '@/components/field'
import { StatusPill } from '@/components/status-pill'
import { useToast } from '@/components/toast'
import { api, apiMutate } from '@/lib/api'
import { fmtDate, fmtDateTime, ORDER_STATUS } from '@/lib/labels'

type AddressRow = {
  id: string
  label: string
  fullName: string
  phone: string
  communeId: string | null
  quarter: string | null
  landmark: string | null
  line1: string
  note: string | null
  isDefault: boolean
  commune: { id: string; name: string } | null
}

type CustomerDetail = {
  id: string
  email: string
  firstName: string
  lastName: string | null
  phone: string | null
  status: string
  source: string
  emailVerifiedAt: string | null
  newsletter: boolean
  purchaseHistory: boolean
  smsOptIn: boolean
  suggestions: boolean
  createdAt: string
  addresses: AddressRow[]
  orders: Array<{
    reference: string
    status: string
    totalCents: number
    placedAt: string
  }>
}

type CommuneRow = { id: string; name: string }

type AddressDraft = {
  id?: string
  label: string
  fullName: string
  phone: string
  communeId: string
  quarter: string
  landmark: string
  line1: string
  note: string
  isDefault: boolean
  delete?: boolean
}

export function ClientDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string }
  const toast = useToast()
  const qc = useQueryClient()

  const customer = useQuery({
    queryKey: ['admin-customer', id],
    queryFn: () => api<CustomerDetail>(`/admin/customers/${id}`),
    enabled: Boolean(id),
  })

  const communes = useQuery({
    queryKey: ['admin-communes'],
    queryFn: () => api<{ items: CommuneRow[] }>('/admin/delivery/communes'),
  })

  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [status, setStatus] = useState('ACTIVE')
  const [newsletter, setNewsletter] = useState(false)
  const [addresses, setAddresses] = useState<AddressDraft[]>([])
  const [resetUrl, setResetUrl] = useState<string | null>(null)

  useEffect(() => {
    const c = customer.data
    if (!c) return
    setEmail(c.email)
    setFirstName(c.firstName)
    setLastName(c.lastName ?? '')
    setPhone(c.phone ?? '')
    setStatus(c.status === 'DISABLED' ? 'DISABLED' : 'ACTIVE')
    setNewsletter(c.newsletter)
    setAddresses(
      (c.addresses ?? []).map((a) => ({
        id: a.id,
        label: a.label,
        fullName: a.fullName,
        phone: a.phone,
        communeId: a.communeId ?? a.commune?.id ?? '',
        quarter: a.quarter ?? '',
        landmark: a.landmark ?? '',
        line1: a.line1,
        note: a.note ?? '',
        isDefault: a.isDefault,
      })),
    )
    setResetUrl(null)
  }, [customer.data])

  const save = useMutation({
    mutationFn: () =>
      apiMutate(`/admin/customers/${id}`, 'PATCH', {
        email,
        firstName,
        lastName: lastName || null,
        phone: phone || null,
        status,
        newsletter,
        addresses: addresses.map((a) => ({
          id: a.id,
          label: a.label,
          fullName: a.fullName,
          phone: a.phone,
          communeId: a.communeId || null,
          quarter: a.quarter || null,
          landmark: a.landmark || null,
          line1: a.line1,
          note: a.note || null,
          isDefault: a.isDefault,
          delete: a.delete || undefined,
        })),
      }),
    onSuccess: () => {
      toast.ok('Cliente enregistrée')
      void qc.invalidateQueries({ queryKey: ['admin-customer', id] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const resetLink = useMutation({
    mutationFn: () =>
      apiMutate<{ url: string }>(`/admin/customers/${id}/password-reset-link`, 'POST', {}),
    onSuccess: (r) => {
      setResetUrl(r.url)
      toast.ok('Lien généré (copiez-le maintenant)')
    },
    onError: (e: Error) => toast.err(e.message),
  })

  if (customer.isError) {
    return (
      <div className="carte-admin p-6">
        <p className="text-rouge-texte">Client introuvable.</p>
        <a href="/clients" className="mt-3 inline-block text-sm text-rouge">
          Retour
        </a>
      </div>
    )
  }

  if (customer.isLoading || !customer.data) {
    return <p className="text-second-admin">Chargement…</p>
  }

  const c = customer.data
  const orders = c.orders ?? []

  function addAddress() {
    setAddresses((prev) => [
      ...prev,
      {
        label: 'Domicile',
        fullName: `${firstName} ${lastName}`.trim() || firstName,
        phone: phone || '',
        communeId: '',
        quarter: '',
        landmark: '',
        line1: '',
        note: '',
        isDefault: prev.filter((a) => !a.delete).length === 0,
      },
    ])
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <a href="/clients" className="kicker text-second-admin hover:text-encre-admin">
          ← Clients
        </a>
        <h2 className="font-titre text-xl text-encre-admin">
          {c.firstName} {c.lastName}
        </h2>
        <StatusPill
          label={c.emailVerifiedAt ? 'E-mail vérifié' : 'Non vérifié'}
          tone={c.emailVerifiedAt ? 'ok' : 'warn'}
        />
        <StatusPill
          label={c.source === 'BACKOFFICE' ? 'Back-office' : 'Boutique'}
          tone="neutral"
        />
        <div className="ml-auto flex flex-wrap gap-2">
          <BtnGhost onClick={() => resetLink.mutate()} disabled={resetLink.isPending}>
            Lien mot de passe
          </BtnGhost>
          <a
            href="/commandes"
            className="kicker rounded-pilule border border-bordure-admin px-3.5 py-2 text-encre-admin"
          >
            Créer une commande
          </a>
          <BtnPrimary disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </BtnPrimary>
        </div>
      </div>

      {resetUrl ? (
        <div className="carte-admin border border-vert-texte/30 bg-vert-fond p-4 text-sm text-vert-texte">
          <p className="font-medium">Lien à usage unique (copiez-le maintenant) :</p>
          <p className="mt-2 break-all font-mono text-xs text-encre-admin">{resetUrl}</p>
        </div>
      ) : null}

      <div className="grid gap-5 lg:grid-cols-2">
        <section className="carte-admin p-4">
          <h3 className="text-base">Coordonnées</h3>
          <div className="mt-3 flex flex-col gap-3">
            <Field label="Prénom">
              <TextInput value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </Field>
            <Field label="Nom">
              <TextInput value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </Field>
            <Field label="E-mail">
              <TextInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            <Field label="Téléphone">
              <TextInput value={phone} onChange={(e) => setPhone(e.target.value)} />
            </Field>
            <Field label="Statut">
              <Select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="ACTIVE">Actif</option>
                <option value="DISABLED">Désactivé</option>
              </Select>
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={newsletter}
                onChange={(e) => setNewsletter(e.target.checked)}
              />
              Newsletter
            </label>
            <p className="text-xs text-second-admin">Inscrit le {fmtDate(c.createdAt)}</p>
          </div>
        </section>

        <section className="carte-admin p-4">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-base">Adresses</h3>
            <BtnGhost onClick={addAddress}>Ajouter</BtnGhost>
          </div>
          <div className="mt-3 flex flex-col gap-4">
            {addresses.filter((a) => !a.delete).length === 0 ? (
              <p className="text-sm text-second-admin">Aucune adresse.</p>
            ) : null}
            {addresses.map((a, idx) =>
              a.delete ? null : (
                <div key={a.id ?? `new-${idx}`} className="rounded-champ border border-bordure-admin p-3">
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Field label="Libellé">
                      <TextInput
                        value={a.label}
                        onChange={(e) =>
                          setAddresses((prev) =>
                            prev.map((x, i) => (i === idx ? { ...x, label: e.target.value } : x)),
                          )
                        }
                      />
                    </Field>
                    <Field label="Nom complet">
                      <TextInput
                        value={a.fullName}
                        onChange={(e) =>
                          setAddresses((prev) =>
                            prev.map((x, i) => (i === idx ? { ...x, fullName: e.target.value } : x)),
                          )
                        }
                      />
                    </Field>
                    <Field label="Téléphone">
                      <TextInput
                        value={a.phone}
                        onChange={(e) =>
                          setAddresses((prev) =>
                            prev.map((x, i) => (i === idx ? { ...x, phone: e.target.value } : x)),
                          )
                        }
                      />
                    </Field>
                    <Field label="Commune">
                      <Select
                        value={a.communeId}
                        onChange={(e) =>
                          setAddresses((prev) =>
                            prev.map((x, i) =>
                              i === idx ? { ...x, communeId: e.target.value } : x,
                            ),
                          )
                        }
                      >
                        <option value="">—</option>
                        {(communes.data?.items ?? []).map((co) => (
                          <option key={co.id} value={co.id}>
                            {co.name}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field label="Adresse">
                      <TextInput
                        value={a.line1}
                        onChange={(e) =>
                          setAddresses((prev) =>
                            prev.map((x, i) => (i === idx ? { ...x, line1: e.target.value } : x)),
                          )
                        }
                      />
                    </Field>
                    <Field label="Repère">
                      <TextInput
                        value={a.landmark}
                        onChange={(e) =>
                          setAddresses((prev) =>
                            prev.map((x, i) =>
                              i === idx ? { ...x, landmark: e.target.value } : x,
                            ),
                          )
                        }
                      />
                    </Field>
                    <Field label="Quartier">
                      <TextInput
                        value={a.quarter}
                        onChange={(e) =>
                          setAddresses((prev) =>
                            prev.map((x, i) => (i === idx ? { ...x, quarter: e.target.value } : x)),
                          )
                        }
                      />
                    </Field>
                    <Field label="Note">
                      <TextArea
                        value={a.note}
                        onChange={(e) =>
                          setAddresses((prev) =>
                            prev.map((x, i) => (i === idx ? { ...x, note: e.target.value } : x)),
                          )
                        }
                      />
                    </Field>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="default-address"
                        checked={a.isDefault}
                        onChange={() =>
                          setAddresses((prev) =>
                            prev.map((x, i) => ({ ...x, isDefault: i === idx })),
                          )
                        }
                      />
                      Par défaut
                    </label>
                    <button
                      type="button"
                      className="text-sm text-rouge"
                      onClick={() =>
                        setAddresses((prev) =>
                          a.id
                            ? prev.map((x, i) => (i === idx ? { ...x, delete: true } : x))
                            : prev.filter((_, i) => i !== idx),
                        )
                      }
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              ),
            )}
          </div>
        </section>
      </div>

      <section className="carte-admin">
        <div className="border-b border-bordure-admin px-4 py-3">
          <h3 className="text-base">Commandes récentes</h3>
        </div>
        {orders.length ? (
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-bordure-admin">
                {['Réf.', 'Statut', 'Total', 'Date'].map((h) => (
                  <th key={h} className="px-4 py-2.5">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const st = ORDER_STATUS[o.status] ?? { label: o.status, tone: 'neutral' }
                return (
                  <tr key={o.reference} className="border-b border-bordure-admin last:border-0">
                    <td className="px-4 py-2.5">
                      <a
                        href={`/commandes/${o.reference}`}
                        className="font-medium text-encre-admin underline-offset-2 hover:underline"
                      >
                        {o.reference}
                      </a>
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusPill label={st.label} tone={st.tone} />
                    </td>
                    <td className="px-4 py-2.5 tabular-nums">{formatMoney(o.totalCents)}</td>
                    <td className="px-4 py-2.5 text-second-admin">{fmtDateTime(o.placedAt)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        ) : (
          <p className="p-4 text-sm text-second-admin">Aucune commande.</p>
        )}
      </section>
    </div>
  )
}
